import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  changeSource,
  type SourceDescriptor,
  type SourceInput,
  type SourceSettings,
} from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { FilthShareBrowser } from "./filth-share-browser"
import { FilthSharePointSitePicker } from "./filth-sharepoint-site-picker"
import {
  bytesToMb,
  editableSourceSettings,
  sourceControlKey as key,
  pathUnderRoots,
  sourceControlError,
  validSourceSettings,
} from "./filth-source-control-utils"
import { FilthSourceSettingsFields } from "./filth-source-settings-fields"

export function FilthAddSourceDialog({
  backendId,
  descriptor,
  onClose,
  onSaved,
}: {
  backendId: string
  descriptor: SourceDescriptor
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [kind, setKind] = useState(descriptor.source_kinds[0] ?? "sharepoint")
  const [app, setApp] = useState(
    descriptor.connector_apps.find((v) => v.kind === "sharepoint")?.name ?? ""
  )
  const [site, setSite] = useState("")
  const [drive, setDrive] = useState("")
  const [root, setRoot] = useState("")
  const [name, setName] = useState("")
  const acl = (kind: string) =>
    kind === "sharepoint"
      ? "rest"
      : (descriptor.supported_acl_modes.find((v) => ["posix", "ntfs_manifest"].includes(v)) ?? "")
  const [settings, setSettings] = useState<SourceSettings>({
    include_extensions: ["pdf", "docx"],
    max_file_bytes: descriptor.ceilings.max_file_bytes,
    interval_seconds: null,
    acl_mode: acl(kind),
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const action = useRef<{ fingerprint: string; key: string } | null>(null)
  const targetValid =
    kind === "sharepoint"
      ? !!app && !!site && !!drive
      : pathUnderRoots(root, descriptor.allowed_share_roots)
  const settingsValid = validSourceSettings(settings, descriptor.ceilings) && !!settings.acl_mode
  async function create() {
    const body: SourceInput = {
      kind,
      ...(name.trim() ? { name: name.trim() } : {}),
      settings: editableSourceSettings(settings),
      ...(kind === "sharepoint"
        ? { app, site_id: site, drive_id: drive }
        : { root_path: root, acl_mode: settings.acl_mode }),
    }
    const fingerprint = JSON.stringify(body)
    if (action.current?.fingerprint !== fingerprint)
      action.current = { fingerprint, key: crypto.randomUUID() }
    setBusy(true)
    setError(null)
    try {
      await changeSource(backendId, "create", { body, key: action.current!.key })
      onSaved()
      onClose()
    } catch (e) {
      setError(sourceControlError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (busy) e.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{t(`${key}.add`)}</DialogTitle>
          <DialogDescription>{t(`${key}.steps.${step}`)}</DialogDescription>
        </DialogHeader>
        {step === 0 && (
          <Label className="grid gap-2">
            {t(`${key}.kind`)}
            <select
              aria-label={t(`${key}.kind`)}
              className="bg-background rounded-md border p-2 text-sm"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value)
                setSettings((v) => ({ ...v, acl_mode: acl(e.target.value) }))
                setSite("")
                setDrive("")
                setRoot("")
              }}
            >
              {descriptor.source_kinds.map((v) => (
                <option key={v} value={v}>
                  {t(`${key}.kinds.${v}`)}
                </option>
              ))}
            </select>
          </Label>
        )}
        {step === 1 &&
          (kind === "sharepoint" ? (
            <div className="space-y-4">
              <Label className="grid gap-2">
                {t(`${key}.app`)}
                <select
                  aria-label={t(`${key}.app`)}
                  className="bg-background rounded-md border p-2 text-sm"
                  value={app}
                  onChange={(e) => {
                    setApp(e.target.value)
                    setSite("")
                    setDrive("")
                  }}
                >
                  <option value="">{t(`${key}.choose`)}</option>
                  {descriptor.connector_apps
                    .filter((v) => v.kind === "sharepoint")
                    .map((v) => (
                      <option key={v.name} value={v.name}>
                        {v.name}
                      </option>
                    ))}
                </select>
              </Label>
              <FilthSharePointSitePicker
                key={app}
                app={app}
                backendId={backendId}
                drive={drive}
                site={site}
                onDrive={setDrive}
                onSite={setSite}
              />
            </div>
          ) : (
            <FilthShareBrowser
              backendId={backendId}
              roots={descriptor.allowed_share_roots}
              value={root}
              onChange={setRoot}
            />
          ))}
        {step === 2 && (
          <div className="space-y-4">
            <Label className="grid gap-2">
              {t(`${key}.name`)}
              <Input maxLength={256} value={name} onChange={(e) => setName(e.target.value)} />
            </Label>
            <FilthSourceSettingsFields
              descriptor={descriptor}
              kind={kind}
              value={settings}
              onChange={setSettings}
            />
          </div>
        )}
        {step === 3 && (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">{t(`${key}.kind`)}</dt>
              <dd>{t(`${key}.kinds.${kind}`)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.target`)}</dt>
              <dd className="break-all">
                {kind === "sharepoint" ? `${app} / ${site} / ${drive}` : root}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.name`)}</dt>
              <dd>{name || "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.fileTypes`)}</dt>
              <dd>{settings.include_extensions.join(", ")}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.maxSize`)}</dt>
              <dd>{bytesToMb(settings.max_file_bytes)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.interval`)}</dt>
              <dd>{settings.interval_seconds ?? t(`${key}.manual`)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t(`${key}.aclMode`)}</dt>
              <dd>{settings.acl_mode}</dd>
            </div>
          </dl>
        )}
        {error && (
          <p className="text-sm" role="alert">
            {t(`${key}.errors.${error}`)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button disabled={busy} variant="outline" onClick={onClose}>
            {t(`${key}.cancel`)}
          </Button>
          {step > 0 && (
            <Button disabled={busy} variant="outline" onClick={() => setStep(step - 1)}>
              {t(`${key}.back`)}
            </Button>
          )}
          {step < 3 ? (
            <Button
              disabled={!([!!kind, targetValid, settingsValid][step] ?? false)}
              onClick={() => setStep(step + 1)}
            >
              {t(`${key}.next`)}
            </Button>
          ) : (
            <Button disabled={busy || !targetValid || !settingsValid} onClick={() => void create()}>
              {t(`${key}.create`)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
